import { Post, Prisma } from "@prisma/client";
import axiosInstance from "./axiosInstance";
import { SubmittedProperty } from "src/Types/submittedProperties.types";
import { CSVRow } from "src/Pages/Services/OneTime";
import {
  CommunityBoundary,
  MapboxLocationFeature,
  MonitoringConfiguration,
  MonitoringConfigurationInput,
} from "src/Types/monitoring.types";
import {
  MonitoringAlertFeed,
  MonitoringScanRun,
  MonitoringScanStatusSummary,
} from "src/Types/alerts.types";
import {
  AdminMember,
  AdminOrganization,
  AuthSession,
  AuthUser,
  CreatedRegistrationCode,
  CreateRegistrationCodeInput,
  LoginInput,
  RegisterInput,
} from "src/Types/auth.types";

type PostWithAuthor = Prisma.PostGetPayload<{
  include: {
    author: true;
  };
}>;

// Submit a contact inquiry (notifies customerservice@strmonitoring.com)
export const submitContactInquiryApi = (
  contact: {
    name: string;
    email: string;
    phone?: string;
    message: string;
  },
  callback: (response: any) => void,
  errorCallback: (error: any) => void
) => {
  axiosInstance
    .post(`/api/interested-emails`, contact)
    .then((res) => {
      callback(res.data);
    })
    .catch((err) => {
      errorCallback(err.response?.data || err.message);
    });
};

// Function to add an email to the /api/interested-emails endpoint
export const getAllPosts = (
  callback: (response: any) => void,
  errorCallback: (error: any) => void
) => {
  axiosInstance
    .get(`/api/posts`)
    .then((res) => {
      callback(res.data); // Return the created email object
    })
    .catch((err) => {
      errorCallback(err.response?.data || err.message); // Handle errors
    });
};

// Function to create a new post
export const createPost = (
  post: Omit<Prisma.PostCreateInput, "author"> & {
    author: Prisma.AuthorCreateInput;
  },
  callback: (response: Post) => void,
  errorCallback: (error: any) => void
) => {
  axiosInstance
    .post<Post>("/api/posts", post)
    .then((res) => {
      callback(res.data); // Handle the created post
    })
    .catch((err) => {
      errorCallback(err.response?.data || err.message); // Handle errors
    });
};

// Function to fetch a single post by slug
export const getPost = (
  slug: string, // Use slug to fetch the post
  callback: (response: PostWithAuthor) => void, // Success callback
  errorCallback: (error: any) => void // Error callback
) => {
  axiosInstance
    .get<PostWithAuthor>(`/api/posts/${slug}`) // Make a GET request using the slug
    .then((res) => {
      callback(res.data); // Pass the fetched post data to the callback
    })
    .catch((err) => {
      errorCallback(err.response?.data || err.message); // Handle errors
    });
};

// Function to create a new post
export const oneTimeServiceSTRLookup = (
  array: CSVRow[] | SubmittedProperty[],
  callback: (response: Post) => void,
  errorCallback: (error: any) => void
) => {
  axiosInstance
    .post("/api/one-time-service", array)
    .then((res) => {
      callback(res.data); // Handle the created post
    })
    .catch((err) => {
      errorCallback(err.response?.data || err.message); // Handle errors
    });
};

export const loginApi = async (input: LoginInput): Promise<AuthSession> => {
  const response = await axiosInstance.post<AuthSession>(
    "/api/auth/login",
    input
  );
  return response.data;
};

export const registerApi = async (
  input: RegisterInput
): Promise<AuthSession> => {
  const response = await axiosInstance.post<AuthSession>(
    "/api/auth/register",
    input
  );
  return response.data;
};

export const getMeApi = async (): Promise<AuthUser> => {
  const response = await axiosInstance.get<{ user: AuthUser }>("/api/auth/me");
  return response.data.user;
};

export const getAdminOrganizations = async (): Promise<AdminOrganization[]> => {
  const response = await axiosInstance.get<AdminOrganization[]>(
    "/api/admin/organizations"
  );
  return response.data;
};

export const getAdminMembers = async (): Promise<AdminMember[]> => {
  const response = await axiosInstance.get<AdminMember[]>("/api/admin/users");
  return response.data;
};

export const createAdminRegistrationCode = async (
  input: CreateRegistrationCodeInput
): Promise<CreatedRegistrationCode> => {
  const response = await axiosInstance.post<CreatedRegistrationCode>(
    "/api/admin/registration-codes",
    input
  );
  return response.data;
};

export const revokeAdminRegistrationCode = async (
  codeId: string
): Promise<void> => {
  await axiosInstance.post(
    `/api/admin/registration-codes/${encodeURIComponent(codeId)}/revoke`
  );
};

export const startAdminOrganizationScan = async (
  organizationId: string
): Promise<MonitoringScanRun> => {
  const response = await axiosInstance.post<MonitoringScanRun>(
    `/api/admin/organizations/${encodeURIComponent(organizationId)}/scan`
  );
  return response.data;
};

export const getMonitoringConfiguration =
  async (): Promise<MonitoringConfiguration | null> => {
    const response = await axiosInstance.get<MonitoringConfiguration | null>(
      "/api/monitoring-configuration"
    );
    return response.data;
  };

export const saveMonitoringConfiguration = async (
  configuration: MonitoringConfigurationInput
): Promise<MonitoringConfiguration> => {
  const response = await axiosInstance.put<MonitoringConfiguration>(
    "/api/monitoring-configuration",
    configuration
  );
  return response.data;
};

export const lookupCommunityBoundary = async (
  location: MapboxLocationFeature
): Promise<CommunityBoundary> => {
  const response = await axiosInstance.post<CommunityBoundary>(
    "/api/monitoring-configuration/community-boundary",
    {
      query: location.place_name,
      center: {
        latitude: location.center[1],
        longitude: location.center[0],
      },
    }
  );
  return response.data;
};

export const searchCommunityBoundaries = async (
  query: string
): Promise<CommunityBoundary[]> => {
  const response = await axiosInstance.post<CommunityBoundary[]>(
    "/api/monitoring-configuration/community-search",
    { query }
  );
  return response.data;
};

export interface CommunityPopulationResult {
  population: number;
  geographyName: string | null;
  geographySource: string;
  acsYear: string;
  landAreaSqMiles: number | null;
}

export const fetchCommunityPopulation = async (center: {
  latitude: number;
  longitude: number;
}): Promise<CommunityPopulationResult> => {
  const response = await axiosInstance.post<CommunityPopulationResult>(
    "/api/monitoring-configuration/community-population",
    center
  );
  return response.data;
};

export const getMonitoringAlerts = async (): Promise<MonitoringAlertFeed> => {
  const response = await axiosInstance.get<MonitoringAlertFeed>(
    "/api/monitoring-configuration/alerts"
  );
  return response.data;
};

export const getMonitoringScanStatus =
  async (): Promise<MonitoringScanStatusSummary> => {
    const response = await axiosInstance.get<MonitoringScanStatusSummary>(
      "/api/monitoring-configuration/scan-status"
    );
    return response.data;
  };

export const acknowledgeMonitoringAlert = async (
  alertId: string
): Promise<void> => {
  await axiosInstance.put(
    `/api/monitoring-configuration/alerts/${encodeURIComponent(alertId)}/read`
  );
};

export const startMonitoringScan = async (): Promise<MonitoringScanRun> => {
  const response = await axiosInstance.post<MonitoringScanRun>(
    "/api/monitoring-configuration/scan"
  );
  return response.data;
};
